import { createContext, useContext, useEffect, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface AppSettings {
  'pricing.midpointFloorPct': number;
  'pricing.vatRate': number;
  'pricing.floorSAR': number;
  'pricing.quantityDiscountTiers': Array<{ minPieces: number; pct: number }>;
  'brand.name': string;
  'brand.nameEn': string;
  'brand.tagline': string;
  'brand.pageTitle': string;
  'site.language': 'ar' | 'en';
  'contact.whatsappNumber': string;
  'contact.whatsappMessage': string;
  'delivery.label': string;
  'delivery.fee': number;
  'links.websiteUrl': string;
  'links.websiteLabel': string;
  'links.showWhatsappOnHome': boolean;
  'brand.logoUrl': string;

  'pillars.icon1': string;
  'pillars.icon2': string;
  'pillars.icon3': string;
  'pillars.icon4': string;
  'theme.homepage': string;
  'theme.primary': string;
  'theme.accent': string;
  'theme.background': string;
  'theme.card': string;
  'theme.savings': string;
  'theme.text': string;

  'copy.heroHeadline': string;
  'copy.heroSubheadline': string;
  'copy.instantBadge': string;
  'copy.pillar1': string;
  'copy.pillar2': string;
  'copy.pillar3': string;
  'copy.pillar4': string;
  'copy.yourPrice': string;
  'copy.marketPrice': string;
  'copy.savingsLabel': string;
  'copy.addToOrder': string;
  'copy.submitOrder': string;
  'copy.noPaymentTitle': string;
  'copy.noPaymentBody': string;
  'copy.loadingTitle': string;
  'copy.whatsappButton': string;

  'pricing.minQuantity': number;
  'pricing.minOrderTotalSAR': number;
  'hero.videoUrl': string;
  'hero.videoEnabled': boolean;
}

const DEFAULTS: AppSettings = {
  'pricing.midpointFloorPct': 35,
  'pricing.vatRate': 15,
  'pricing.floorSAR': 500,
  'pricing.quantityDiscountTiers': [
    { minPieces: 2, pct: 5 },
    { minPieces: 3, pct: 10 },
    { minPieces: 4, pct: 15 },
  ],
  'brand.name': 'سنديان',
  'brand.nameEn': 'Sindian',
  'brand.tagline': 'من المصنع مباشرة · سعر فوري',
  'brand.pageTitle': 'سِنديان — أثاث مفصّل بسعر المصنع',
  'site.language': 'ar',
  'contact.whatsappNumber': '966500000000',
  'contact.whatsappMessage': 'مرحبًا، طلبي: {{orderLink}} — أرغب بمتابعة طلب التفصيل.',
  'delivery.label': 'توصيل مجاني',
  'delivery.fee': 0,
  'links.websiteUrl': '',
  'links.websiteLabel': 'تصفّح مجموعاتنا',
  'links.showWhatsappOnHome': true,
  'brand.logoUrl': '',

  'pillars.icon1': 'ruler',
  'pillars.icon2': 'price',
  'pillars.icon3': 'star',
  'pillars.icon4': 'truck',
  'theme.homepage': 'classic',
  'theme.primary': '#4d301a',
  'theme.accent': '#c9904e',
  'theme.background': '#fbf7f2',
  'theme.card': '#f5ebdd',
  'theme.savings': '#6b7f5a',
  'theme.text': '#2e1d10',

  'copy.heroHeadline': '',
  'copy.heroSubheadline': '',
  'copy.instantBadge': '',
  'copy.pillar1': '',
  'copy.pillar2': '',
  'copy.pillar3': '',
  'copy.pillar4': '',
  'copy.yourPrice': '',
  'copy.marketPrice': '',
  'copy.savingsLabel': '',
  'copy.addToOrder': '',
  'copy.submitOrder': '',
  'copy.noPaymentTitle': '',
  'copy.noPaymentBody': '',
  'copy.loadingTitle': '',
  'copy.whatsappButton': '',

  'pricing.minQuantity': 1,
  'pricing.minOrderTotalSAR': 0,
  'hero.videoUrl': '',
  'hero.videoEnabled': false,
};

const SettingsCtx = createContext<AppSettings>(DEFAULTS);

const THEME_KEYS: Array<[keyof AppSettings, string]> = [
  ['theme.primary', '--c-primary'],
  ['theme.accent', '--c-accent'],
  ['theme.background', '--c-bg'],
  ['theme.card', '--c-card'],
  ['theme.savings', '--c-savings'],
  ['theme.text', '--c-text'],
];

export function SettingsProvider({ children }: PropsWithChildren) {
  const { data } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => {
      const res = await fetch('/api/settings');
      if (!res.ok) return DEFAULTS;
      const body = (await res.json()) as { settings: Partial<AppSettings> };
      return { ...DEFAULTS, ...body.settings };
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const settings = data ?? DEFAULTS;

  // Inject theme CSS vars onto <html>
  useEffect(() => {
    const root = document.documentElement;
    for (const [key, varName] of THEME_KEYS) {
      const val = settings[key] as string;
      if (val) root.style.setProperty(varName, val);
    }
    // Also set bg on body for immediate effect
    root.style.setProperty('background-color', `var(--c-bg, ${DEFAULTS['theme.background']})`);
  }, [settings]);

  return <SettingsCtx.Provider value={settings}>{children}</SettingsCtx.Provider>;
}

export function useSettings(): AppSettings {
  return useContext(SettingsCtx);
}

export function useSetting<K extends keyof AppSettings>(key: K): AppSettings[K] {
  return useContext(SettingsCtx)[key];
}

export const COPY_MAP: Record<string, string> = {
  'copy.heroHeadline': 'landing.headline',
  'copy.heroSubheadline': 'landing.subheadline',
  'copy.instantBadge': 'landing.instant_badge',
  'copy.pillar1': 'landing.pillars.custom',
  'copy.pillar2': 'landing.pillars.price',
  'copy.pillar3': 'landing.pillars.quality',
  'copy.pillar4': 'landing.pillars.delivery',
  'copy.yourPrice': 'quote.your_price',
  'copy.marketPrice': 'quote.market_price',
  'copy.savingsLabel': 'quote.savings_label',
  'copy.addToOrder': 'quote.cta',
  'copy.submitOrder': 'cart.submit',
  'copy.noPaymentTitle': 'cart.soft_note_title',
  'copy.noPaymentBody': 'cart.soft_note_body',
  'copy.loadingTitle': 'loading.title',
  'copy.whatsappButton': 'order_confirmed.whatsapp',
};

export function useCopy(copyKey: string): string {
  const { t } = useTranslation();
  const settings = useContext(SettingsCtx);
  const adminValue = settings[copyKey as keyof AppSettings] as string | undefined;
  if (adminValue && adminValue.trim()) return adminValue;
  const i18nKey = COPY_MAP[copyKey];
  return i18nKey ? t(i18nKey) : '';
}
